/**
 * Statsig Feature Flag Adapter
 *
 * This file contains ALL Statsig-specific code.
 * To switch to another provider (LaunchDarkly, etc.), replace ONLY this file.
 */
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { StatsigClient } from '@statsig/js-client';
import { getAppEnvironment } from '@/config/env-config';
import type { FeatureFlag } from '@/enums/feature-flag';
import { getStatsigClientKey } from '@/providers/adapters/statsig-config';
import type { FeatureFlagContextValue } from '@/types/context/feature-flag-context.types';

interface StatsigAdapterProps {
  children: (value: FeatureFlagContextValue) => React.ReactNode;
  userId?: string;
  tenantSlug?: string | null;
}

/**
 * Statsig adapter that initializes the SDK and provides feature flag evaluation.
 * Uses render props pattern to pass the context value to the parent provider.
 *
 * Lifecycle: one StatsigClient per mount. User/tenant changes route through
 * updateUserAsync on the existing instance (Statsig best practice — avoids
 * cache-key collisions and lost analytics on re-instantiation).
 */
export function StatsigAdapter({
  children,
  userId,
  tenantSlug,
}: StatsigAdapterProps) {
  'use no memo';

  const [client, setClient] = useState<StatsigClient | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [evalRevision, setEvalRevision] = useState(0);

  const createClient = useEffectEvent(
    () =>
      new StatsigClient(
        getStatsigClientKey(),
        {
          userID: userId,
          customIDs: { tenant_slug: tenantSlug ?? '' },
        },
        {
          environment: { tier: getAppEnvironment() },
        }
      )
  );

  useEffect(() => {
    const statsigClient = createClient();
    let cancelled = false;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- the effect run that creates the client must also shut it down
    setClient(statsigClient);

    statsigClient
      .initializeAsync()
      .catch(error => {
        if (!cancelled) {
          console.error('[FeatureFlags] Statsig initialization failed:', error);
        }
      })
      // Settle readiness whether init succeeds or fails so consumers never hang;
      // a failed init leaves gates evaluating to their default (false).
      .finally(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      });

    return (): void => {
      cancelled = true;
      statsigClient.shutdown();
      setClient(null);
    };
  }, []);

  useEffect(() => {
    if (!isReady || !client) {
      return;
    }

    let cancelled = false;

    client
      .updateUserAsync({
        userID: userId,
        customIDs: { tenant_slug: tenantSlug ?? '' },
      })
      .then(() => {
        if (!cancelled) {
          setEvalRevision(revision => revision + 1);
        }
      })
      .catch(error => {
        console.error('[FeatureFlags] Statsig updateUserAsync failed:', error);
      });

    return (): void => {
      cancelled = true;
    };
  }, [client, userId, tenantSlug, isReady]);

  const value: FeatureFlagContextValue = useMemo(
    () => ({
      isReady,
      isFeatureEnabled: (flag: FeatureFlag): boolean => {
        if (!isReady || !client) {
          return false;
        }

        try {
          return client.checkGate(flag);
        } catch (error) {
          console.error(`[FeatureFlags] Error checking gate "${flag}":`, error);

          return false;
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evalRevision invalidates context after user update
    [client, isReady, evalRevision]
  );

  return <>{children(value)}</>;
}
